import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { OperatorProvider, useOperator } from "@/context/OperatorContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Zap, Gauge, Building2, LayoutGrid, ClipboardList, Bike, ShieldAlert, Wrench, Globe, Settings, LogOut, Loader2, AlertTriangle, Receipt, BarChart3, Truck, Menu } from "lucide-react";

const ICONS = { Zap, Gauge, Building2 };

const MODULE_TITLES = {
  "/app": "Live board", "/app/rentals": "Rentals", "/app/recovery": "Recovery",
  "/app/maintenance": "Maintenance", "/app/tourist-bookings": "Tourist", "/app/business": "Business",
  "/app/delivery": "Delivery", "/app/fleet": "Fleet", "/app/analytics": "Analytics",
  "/app/billing": "Billing", "/app/settings": "Settings",
};

const inr = (v) => `₹${(v || 0).toLocaleString("en-IN")}`;

function BillingBanner({ alert }) {
  if (!alert) return null;
  return (
    <div data-testid="operator-billing-banner"
      className="flex items-center gap-2 border-b border-status-overdue/30 bg-status-overdue/10 px-6 py-2.5 text-sm text-status-overdue">
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span>
        Payment overdue — {inr(alert.overdue_amount)} across {alert.overdue_count} platform invoice{alert.overdue_count > 1 ? "s" : ""}.
        Please settle to avoid service suspension.
      </span>
    </div>
  );
}

function NavItem({ to, end, icon: Icon, label, onNavigate }) {
  return (
    <NavLink to={to} end={end} onClick={onNavigate} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-2.5 rounded-tenant-button px-3.5 py-2.5 text-sm font-semibold transition-all duration-300 ease-spring ${
          isActive
            ? "bg-tenant-primary text-tenant-primary-foreground shadow-tenant-soft"
            : "text-muted-foreground hover:bg-tenant-primary/10 hover:text-tenant-primary hover:translate-x-0.5"
        }`
      }>
      <Icon className="h-[18px] w-[18px]" strokeWidth={2.25} /> {label}
    </NavLink>
  );
}

function NavLinks({ featureEnabled, onNavigate }) {
  return (
    <nav className="space-y-1.5">
      <NavItem to="/app" end icon={LayoutGrid} label="Live board" onNavigate={onNavigate} />
      <NavItem to="/app/rentals" icon={ClipboardList} label="Rentals" onNavigate={onNavigate} />
      <NavItem to="/app/recovery" icon={ShieldAlert} label="Recovery" onNavigate={onNavigate} />
      {featureEnabled("maintenance") && <NavItem to="/app/maintenance" icon={Wrench} label="Maintenance" onNavigate={onNavigate} />}
      {featureEnabled("tourist_booking") && <NavItem to="/app/tourist-bookings" icon={Globe} label="Tourist" onNavigate={onNavigate} />}
      {featureEnabled("business_fleet") && <NavItem to="/app/business" icon={Building2} label="Business" onNavigate={onNavigate} />}
      {featureEnabled("delivery") && <NavItem to="/app/delivery" icon={Truck} label="Delivery" onNavigate={onNavigate} />}
      <NavItem to="/app/fleet" icon={Bike} label="Fleet" onNavigate={onNavigate} />
      <NavItem to="/app/analytics" icon={BarChart3} label="Analytics" onNavigate={onNavigate} />
      <NavItem to="/app/billing" icon={Receipt} label="Billing" onNavigate={onNavigate} />
      <NavItem to="/app/settings" icon={Settings} label="Settings" onNavigate={onNavigate} />
    </nav>
  );
}

function Brand({ Icon, ctx }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] bg-tenant-primary text-tenant-primary-foreground shadow-tenant-soft">
        <Icon className="h-5 w-5" strokeWidth={2.25} />
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-bold leading-tight tracking-tight">{ctx.branding?.logo_text}</p>
        <p className="truncate text-xs text-muted-foreground">{ctx.branding?.tagline}</p>
      </div>
    </div>
  );
}

function Shell() {
  const { ctx, featureEnabled } = useOperator();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const moduleLabel = useMemo(() => {
    const p = location.pathname;
    if (p.startsWith("/app/vehicle/")) return "Vehicle history";
    return MODULE_TITLES[p] || "Dashboard";
  }, [location.pathname]);
  useEffect(() => {
    document.title = `${moduleLabel} · ${ctx?.tenant?.name || "FleetOS by Graphikpro"}`;
  }, [moduleLabel, ctx]);

  if (!ctx) {
    return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }
  const Icon = ICONS[ctx.branding?.logo_icon] || Building2;

  return (
    <div className="flex min-h-screen bg-tenant-bg">
      <aside className="sticky top-3 m-3 hidden h-[calc(100vh-1.5rem)] w-64 flex-col rounded-[26px] border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft lg:flex">
        <Brand Icon={Icon} ctx={ctx} />
        <div className="mt-8">
          <NavLinks featureEnabled={featureEnabled} />
        </div>
        <div className="mt-auto flex items-center justify-between rounded-tenant-button bg-tenant-primary/8 px-3.5 py-2.5 text-xs font-semibold text-tenant-primary">
          <span>{ctx.tenant.plan_name} plan</span>
          <span className="h-2 w-2 rounded-full bg-status-available" />
        </div>
      </aside>

      <div className="min-w-0 flex-1 lg:py-3 lg:pr-3">
        <header className="sticky top-0 z-20 flex items-center justify-between gap-2 border-b border-tenant-border bg-tenant-surface/90 px-4 py-3 shadow-tenant-soft backdrop-blur-xl sm:px-5 lg:top-3 lg:rounded-[20px] lg:border">
          <div className="flex min-w-0 items-center gap-2">
            {/* mobile nav trigger */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button data-testid="mobile-nav-trigger" variant="ghost" size="icon" className="shrink-0 lg:hidden">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" data-testid="mobile-nav-drawer" className="w-72 border-tenant-border bg-tenant-surface p-5">
                <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                <SheetDescription className="sr-only">Browse FleetOS modules</SheetDescription>
                <div className="mb-6"><Brand Icon={Icon} ctx={ctx} /></div>
                <NavLinks featureEnabled={featureEnabled} onNavigate={() => setMobileOpen(false)} />
                <div className="mt-6 flex items-center justify-between rounded-tenant-button bg-tenant-primary/8 px-3.5 py-2.5 text-xs font-semibold text-tenant-primary">
                  <span>{ctx.tenant.plan_name} plan</span>
                  <span className="h-2 w-2 rounded-full bg-status-available" />
                </div>
              </SheetContent>
            </Sheet>
            <div className="min-w-0">
              <p className="truncate font-bold tracking-tight">{ctx.branding?.app_title}</p>
              <p className="truncate text-xs text-muted-foreground">{user?.name} · {user?.role}</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <ThemeToggle />
            <Button data-testid="logout-btn" variant="ghost" size="sm" onClick={logout} className="gap-1.5">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </header>
        <BillingBanner alert={ctx.billing_alert} />
        <main className="p-4 sm:p-6 lg:px-6 lg:pt-6"><Outlet /></main>
      </div>
    </div>
  );
}

export default function OperatorLayout() {
  return (
    <OperatorProvider>
      <Shell />
    </OperatorProvider>
  );
}
